export type FilterParam<T> = {
	/** имя параметра в адресе */
	name: string;
	parse(raw: string | null): T | undefined;
	/** undefined — параметр не пишется в адрес */
	serialize(value: T | undefined): string | undefined;
};

export type FilterSchema = Record<string, FilterParam<any>>;

export type FilterValues<S extends FilterSchema> = {
	[K in keyof S]?: S[K] extends FilterParam<infer T> ? T : never;
};

const NULL_TOKEN = 'none';

const splitList = (raw: string | null) => (raw ? raw.split(',').filter(Boolean) : []);

export function enumList<E extends string>(name: string, values: readonly E[]): FilterParam<E[]> {
	return {
		name,
		parse: (raw) => {
			const list = splitList(raw).filter((value): value is E => values.includes(value as E));
			return list.length ? list : undefined;
		},
		serialize: (value) => (value?.length ? value.join(',') : undefined),
	};
}

export function nullableEnumList<E extends string>(name: string, values: readonly E[]): FilterParam<Array<E | null>> {
	return {
		name,
		parse: (raw) => {
			const list = splitList(raw)
				.map((value) => (value === NULL_TOKEN ? null : value))
				.filter((value): value is E | null => value === null || values.includes(value as E));
			return list.length ? list : undefined;
		},
		serialize: (value) => (value?.length ? value.map((item) => item ?? NULL_TOKEN).join(',') : undefined),
	};
}

export function intList(name: string, values: readonly number[]): FilterParam<number[]> {
	return {
		name,
		parse: (raw) => {
			const list = splitList(raw)
				.map(Number)
				.filter((value) => values.includes(value));
			return list.length ? list : undefined;
		},
		serialize: (value) => (value?.length ? value.join(',') : undefined),
	};
}

export function flag(name: string): FilterParam<boolean> {
	return {
		name,
		parse: (raw) => (raw === '1' ? true : undefined),
		serialize: (value) => (value ? '1' : undefined),
	};
}

export function parseFilters<S extends FilterSchema>(schema: S, search: URLSearchParams): FilterValues<S> {
	const values: Record<string, unknown> = {};
	for (const key in schema) {
		const value = schema[key].parse(search.get(schema[key].name));
		if (value !== undefined) values[key] = value;
	}
	return values as FilterValues<S>;
}

export function serializeFilters<S extends FilterSchema>(
	schema: S,
	values: FilterValues<S>,
	search: URLSearchParams,
): URLSearchParams {
	const next = new URLSearchParams(search);
	for (const key in schema) {
		next.delete(schema[key].name);
		const raw = schema[key].serialize(values[key]);
		if (raw !== undefined) next.set(schema[key].name, raw);
	}
	return next;
}
